import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';

import { SidebarLobbySection, SidebarPromotionsSection, SidebarWinnersSection } from './sections';
import { ReplayDialogPopup, ReplayFramePopup } from './sections/winners/popups';
import { SideBarNavigationKey, SidebarOpenSource } from '../../../../enums';
import { useStore, useOutsideClick, useTranslation } from '../../../../hooks';
import { SideBarNavigation } from "./sections/sidebar-navigation";
import { AspectRatioImage } from '../cashback/ratio-image';
import { SidebarMenuOpener } from './opener-widget';
import { isMobile } from "../../../../helpers";
import { SidebarMenuHeader } from './header';
import { T } from '../../../../constants';

import './style.scss';

const NAV_TITLE_KEYS: Record<SideBarNavigationKey, string> = {
  [SideBarNavigationKey.Lobby]: T.LOBBY,
  [SideBarNavigationKey.Promotions]: T.PASCAL_PROMOTIONS,
  [SideBarNavigationKey.Winners]: T.TOP_WINNERS,
};

interface SidebarMenuProps {
  showLobby: boolean;
  hasPromo: boolean;
  hasJackpot: boolean;
  hasPGWheel: boolean;
  hasPreLobby: boolean;
  hasFreebet: boolean;
  hasTournament: boolean;
  hasTopWinners: boolean;
  showSidebarOpener: boolean;
  showPascalPromotions: boolean;
}

export const SidebarMenu: React.FC<SidebarMenuProps> = observer(({
  showLobby,
  hasPromo,
  hasJackpot,
  hasPGWheel,
  hasPreLobby,
  hasFreebet,
  hasTournament,
  hasTopWinners,
  showSidebarOpener,
  showPascalPromotions,
}) => {
  const t = useTranslation();
  const { sidebarMenuStore, gameStore, lobbyStore } = useStore();
  const { isSidebarOpen, notifCount, activeSidebarIcons, activeTab } = sidebarMenuStore;
  const { launchData: { data: launchData }, gameReady, canvasWidth } = gameStore;

  // The tab lives in the store so open/close events can report it. The default is picked once, at mount.
  const defaultNavItem = showPascalPromotions
    ? SideBarNavigationKey.Promotions
    : hasTopWinners ? SideBarNavigationKey.Winners : SideBarNavigationKey.Lobby;
  const activeNavItem = activeTab || defaultNavItem;

  useEffect(() => {
    if (!sidebarMenuStore.activeTab) sidebarMenuStore.setActiveTab(defaultNavItem);
  }, []);

  useEffect(() => {
    if (!launchData) return;
    if (!!launchData.lobbyId) sidebarMenuStore.fetchNewGames(launchData.partnerId);
    if (launchData.hasTournament) sidebarMenuStore.fetchNewTournaments();
  }, [launchData]);

  // opening a tab clears its notification badge
  useEffect(() => {
    if (!isSidebarOpen) return;
    if (activeNavItem === SideBarNavigationKey.Promotions) {
      sidebarMenuStore.setHasPromotionsNotification(false);
    } else if (activeNavItem === SideBarNavigationKey.Winners) {
      sidebarMenuStore.setHasTopWinsNotification(false);
    }
  }, [isSidebarOpen, activeNavItem]);

  const handleOpen = useCallback(() => sidebarMenuStore.openSidebar(SidebarOpenSource.Button), []);

  const handleOpenLobbyTab = useCallback(() => {
    if (!isMobile()) return;
    sidebarMenuStore.setActiveTab(SideBarNavigationKey.Lobby);
    sidebarMenuStore.setIsSidebarOpen(true, SidebarOpenSource.Lobby);
  }, []);

  const handleClose = useCallback(() => sidebarMenuStore.setIsSidebarOpen(false), []);

  const closeIfOpen = useCallback(() => {
    if (isSidebarOpen) handleClose();
  }, [isSidebarOpen, handleClose]);

  const handleNavigationChange = useCallback((key: SideBarNavigationKey) => {
    sidebarMenuStore.trackTabClick(key);
    if (key === SideBarNavigationKey.Lobby && !isMobile()) {
      lobbyStore.setIsLobbyOpen(true);
      handleClose();
    } else {
      sidebarMenuStore.setActiveTab(key);
    }
  }, [handleClose]);

  const { modalRef, containerRef } = useOutsideClick(closeIfOpen);

  const bannerPopup = useMemo(() => {
    return launchData?.popUps?.find(({ bannerUrl }) => bannerUrl);
  }, [launchData]);

  if (!gameReady) return null;

  const isWebWithCanvas = !isMobile() && !!canvasWidth;

  const content = (<>
    <div
      className="side-menu-new-container"
      style={isWebWithCanvas ? { maxWidth: canvasWidth } : {}}
    >
      <ReplayDialogPopup />
      <ReplayFramePopup />
      {!isMobile() && <AspectRatioImage />}
      <div
        ref={containerRef}
        className={`side-menu-new-wrapper ${isSidebarOpen ? 'wr-open' : ''}`}
      >
        <div className={`side-menu-new ${isSidebarOpen ? 'open' : ''}`} ref={modalRef}>
          <SidebarMenuHeader
            title={t(NAV_TITLE_KEYS[activeNavItem])}
            bannerPopup={bannerPopup}
            onClose={handleClose}
          />
          <div className="side-menu-new-content-holder">
            <div className="side-menu-new-content-holder-top">
              <SideBarNavigation
                showLobby={showLobby}
                activeItem={activeNavItem}
                onSelect={handleNavigationChange}
                showTopWinners={hasTopWinners}
                showPascalPromotions={showPascalPromotions}
              />
            </div>
            {activeNavItem === SideBarNavigationKey.Lobby && <SidebarLobbySection />}
            {activeNavItem === SideBarNavigationKey.Promotions && (
              <SidebarPromotionsSection
                hasPromo={hasPromo}
                hasJackpot={hasJackpot}
                hasPGWheel={hasPGWheel}
                hasFreebet={hasFreebet}
                hasPreLobby={hasPreLobby}
                hasTournament={hasTournament}
                onClose={handleClose}
              />
            )}
            {activeNavItem === SideBarNavigationKey.Winners && <SidebarWinnersSection />}
          </div>
        </div>
      </div>
    </div>
    <SidebarMenuOpener
      onOpen={handleOpen}
      onOpenLobbyTab={handleOpenLobbyTab}
      notifCount={notifCount}
      icons={activeSidebarIcons}
      showSidebarOpener={showSidebarOpener}
    />
  </>)
  if (isWebWithCanvas) {
    return (<div className="is-mobile sidebar-canvas-mobile">{content}</div>)
  }
  return content;
});
